import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useFilters, useGlobalFilter, useSortBy, useTable } from "react-table";
import { Input, Table } from "semantic-ui-react";
import { exists } from "../../utils/utils";

export const DefaultColumnFilter = ({ column: { filterValue, preFilteredRows, setFilter } }) => {
  const count = preFilteredRows.length;
  return (
    <Input
      value={filterValue || ""}
      onChange={event => {
        setFilter(event.target.value || undefined);
      }}
      placeholder={`Search ${count} records...`}
    />
  );
};
export const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <Input
      fluid
      value={globalFilter || ""}
      onChange={event => {
        setGlobalFilter(event.target.value || undefined);
      }}
      placeholder={`Search...`}
      style={{
        border: "0"
      }}
    />
  );
};

export default function SearchableTable({
  columns,
  data,
  filterTypesConditions = {},
  maxHeight,
  getRowId
}) {
  const filterTypes = useMemo(
    () =>
      Object.keys(filterTypesConditions).reduce((filterTypes, type) => {
        const condition = filterTypesConditions[type];
        const newFilterTypeFunction = (rows, id, filterValue) => {
          return rows.filter(row => {
            const rowValue = row.values[id];
            return exists(rowValue) ? condition(rowValue, filterValue) : false;
          });
        };
        return Object.assign(filterTypes, { [type]: newFilterTypeFunction });
      }, {}),
    [filterTypesConditions]
  );

  const globalFilter = (rows, ids, filterValue) => {
    return rows.filter(row =>
      row.cells.reduce(
        (recursion, cell) =>
          recursion ||
          (cell.column &&
            !cell.column.disableFilters &&
            (!exists(filterTypesConditions[cell.column.filter]) ||
              filterTypesConditions[cell.column.filter](cell.value, filterValue))),
        false
      )
    );
  };

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      globalFilter,
      filterTypes,
      getRowId
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  const isFilterResult = (cellValue, filterCondition, filterValue) => {
    return filterCondition(cellValue, filterValue);
  };
  // Render the UI for your table
  return (
    <>
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <div style={{ maxHeight: maxHeight }}>
        <Table celled {...getTableProps()}>
          <Table.Header>
            {headerGroups.map((headerGroup, headerIndex) => (
              <Table.Row key={headerIndex} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, headerCellIndex) => (
                  <Table.HeaderCell
                    key={headerCellIndex}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render("Header")}
                    {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            ))}
          </Table.Header>

          <Table.Body {...getTableBodyProps()}>
            {rows.map((row, index) => {
              prepareRow(row);
              return (
                <Table.Row key={index} {...row.getRowProps()}>
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <Table.Cell
                        key={cellIndex}
                        {...cell.getCellProps()}
                        positive={
                          !cell.column.disableFilters &&
                          isFilterResult(
                            cell.value,
                            filterTypesConditions[cell.column.filter],
                            state.globalFilter
                          )
                        }
                      >
                        {cell.render("Cell")}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
}

SearchableTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterTypesConditions: PropTypes.object.isRequired
};
